import { Check, X, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import { Database } from '../../types/supabase';
import ActivityDocumentButton from '../dashboard/ActivityDocumentButton';
import { formatPersonName } from '../../utils/formatName';

type Event = Database['public']['Functions']['get_recent_activity']['Returns'][number];
const BATCH = 100;
export function decisionEvents(events: Event[], decision: string) {
  return events.filter(event =>
    (event.activity_type === 'document_approved' || event.activity_type === 'document_rejected') &&
    (decision === 'all' || event.activity_type === decision),
  );
}
function field(event: Event, name: string) {
  const metadata = event.metadata;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  return typeof metadata[name] === 'string' ? (metadata[name] as string).trim() || null : null;
}
function timestamp(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Fecha no disponible' : date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}
export default function ReviewActivity() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const period = params.get('activityPeriod') === 'all' ? 'all' : '7d';
  const decision = ['document_approved', 'document_rejected'].includes(params.get('decision') || '') ? params.get('decision')! : 'all';
  const requestedLimit = Number(params.get('activityLimit'));
  const limit = Number.isSafeInteger(requestedLimit) && requestedLimit >= BATCH ? requestedLimit : BATCH;
  const update = (values: Record<string, string>) => setParams(previous => {
    const next = new URLSearchParams(previous);
    Object.entries(values).forEach(([key, value]) => next.set(key, value));
    return next;
  }, { replace: true });
  const [revision, setRevision] = useState(0);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [more, setMore] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    api.getRecentActivity(limit, period).then(data => {
      if (!active) return;
      setEvents(data);
      setMore(data.length >= limit);
    }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [limit, period, revision]);
  const visible = decisionEvents(events, decision);
  return (
    <section id="review-activity" tabIndex={-1} aria-labelledby="review-activity-title" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <h2 id="review-activity-title" className="sr-only">Historial de decisiones</h2>
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4 sm:px-5">
        <label className="min-w-0"><span className="sr-only">Periodo de actividad</span>
          <select className="max-w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={period} onChange={event => { setEvents([]); setMore(false); update({ activityPeriod: event.target.value, activityLimit: String(BATCH) }); }}>
            <option value="7d">Últimos 7 días</option><option value="all">Todo el historial</option>
          </select>
        </label>
        <label className="min-w-0"><span className="sr-only">Decisión</span>
          <select className="max-w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={decision} onChange={event => update({ decision: event.target.value })}>
            <option value="all">Todas las decisiones</option><option value="document_approved">Aprobaciones</option><option value="document_rejected">Rechazos</option>
          </select>
        </label>
        <span role="status" className="text-xs text-slate-500 sm:ml-auto">{loading ? 'Cargando…' : error ? 'Sin actualizar' : `${visible.length} ${visible.length===1?'decisión':'decisiones'}`}</span>
        <button type="button" disabled={loading} title="Actualizar actividad" aria-label="Actualizar actividad" onClick={()=>setRevision(value=>value+1)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:opacity-50"><RefreshCw size={16} aria-hidden="true"/></button>
      </div>
      {error && <p role="alert" className="px-5 py-4 text-sm text-red-800">No se pudo actualizar la actividad. <button className="underline" onClick={() => setRevision(value => value + 1)}>Reintentar actividad</button>{events.length>0&&<span className="mt-1 block text-xs text-slate-500">Se muestra la última consulta disponible.</span>}</p>}
      {!loading && !error && visible.length === 0 && <p className="px-5 py-8 text-sm text-slate-500">{more ? 'No hay coincidencias entre los eventos cargados.' : 'No hay decisiones coincidentes en el periodo seleccionado.'}</p>}
      {visible.length>0&&<div aria-hidden="true" className="hidden grid-cols-[minmax(0,1fr)_170px_180px] gap-6 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-medium text-slate-500 lg:grid"><span>Documento</span><span>Revisor</span><span>Fecha</span></div>}
      <ul aria-label="Registros de aprobaciones y rechazos" className="divide-y divide-slate-100">
        {visible.map((event,index)=>{
          const approved=event.activity_type==='document_approved';
          const actor=formatPersonName(field(event,approved?'approved_by':'rejected_by'));
          const reason=field(event,'rejection_reason');
          const documentId=field(event,'document_id');
          const title=field(event,'document_name') || event.description.replace(/^Documento /,'').replace(/ (aprobado|rechazado)$/,'');
          const Icon=approved?Check:X;
          return <li key={`${event.id}-${event.activity_timestamp}-${index}`} className="grid min-w-0 gap-4 px-5 py-5 lg:grid-cols-[minmax(0,1fr)_170px_180px] lg:gap-6">
            <div className="min-w-0">
              <div className="flex items-start gap-2.5">
                <span title={approved?'Aprobado':'Rechazado'} className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${approved?'bg-emerald-50 text-emerald-700':'bg-red-50 text-red-700'}`}><Icon size={13} aria-hidden="true"/><span className="sr-only">{approved?'Aprobado':'Rechazado'}</span></span>
                <div className="min-w-0"><p className="break-words text-sm font-medium text-slate-900">{title}</p>
                  <p className="mt-1 break-words text-sm text-slate-600">{formatPersonName(event.user_name) || event.user_email || 'Usuario no disponible'}</p>
                  {event.organization_name&&<p className="mt-0.5 text-xs text-slate-500">{event.organization_name}</p>}
                  {!approved&&<p className="mt-3 whitespace-pre-wrap break-words border-l-2 border-slate-200 pl-3 text-sm leading-relaxed text-slate-600"><span className="sr-only">Motivo: </span>{reason || 'Motivo no registrado'}</p>}
                </div>
              </div>
            </div>
            <div className="min-w-0 pl-[30px] lg:pl-0"><p className="mb-1 text-xs text-slate-500 lg:sr-only">Revisor</p><p className="break-words text-sm text-slate-700" title={actor?undefined:'Este registro no conserva la identidad del revisor.'}>{actor || 'Sin registro'}</p></div>
            <div className="pl-[30px] lg:pl-0"><time dateTime={event.activity_timestamp} className="text-xs text-slate-500">{timestamp(event.activity_timestamp)}</time>
              {documentId&&<ActivityDocumentButton documentId={documentId} returnTo={`/admin/activity${location.search}#review-activity`}/>}
            </div>
          </li>;
        })}
      </ul>
      {more&&<div className="border-t border-slate-100 px-5 py-4"><button disabled={loading} onClick={()=>update({activityLimit:String(limit+BATCH)})} className="rounded-md px-2 py-1 text-sm font-medium text-blue-700 hover:bg-slate-50 disabled:opacity-50">Buscar más eventos</button></div>}
    </section>
  );
}
